/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, Eye, EyeOff, MessageCircle, RefreshCw, Play, QrCode, ArrowLeft, Server, ToggleLeft, X, FlaskConical, Plus, Trash2 } from 'lucide-react';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WA_FIELDS = [
  'whatsapp_engine', 'waha_base_url', 'waha_api_key', 'waha_session',
  'whatsapp_invio_attivo', 'whatsapp_solo_pazienti_registrati',
  'whatsapp_ricezione_attiva', 'whatsapp_auto_reply_enabled',
  'whatsapp_test_mode', 'whatsapp_test_numbers',
];

function sessionLabel(s) {
  if (s === 'WORKING') return 'Collegata ✓';
  if (s === 'SCAN_QR_CODE') return 'In attesa scansione QR';
  if (s === 'UNREACHABLE') return 'Server non raggiungibile — controlla tunnel/URL';
  return s;
}

/**
 * Pagina di configurazione WhatsApp (motore, WAHA, interruttori globali).
 * Raggiungibile dall'ingranaggio nella pagina WhatsApp: /backoffice/whatsapp/configurazione
 */
export default function ConfigurazioneWhatsApp() {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [wahaBusy, setWahaBusy] = useState(false);
  const [wahaStatus, setWahaStatus] = useState(null);
  const [wahaQr, setWahaQr] = useState(null);
  const [qrVisible, setQrVisible] = useState(false);
  const [newTestNumber, setNewTestNumber] = useState('');

  const load = () => {
    setLoading(true);
    setLoadError(false);
    axios.get(`${API}/settings`)
      .then(r => {
        const picked = {};
        WA_FIELDS.forEach(k => { picked[k] = r.data?.[k]; });
        setForm(picked);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const testNumbers = Array.isArray(form?.whatsapp_test_numbers) ? form.whatsapp_test_numbers : [];
  const addTestNumber = () => {
    const raw = newTestNumber.trim();
    const digits = raw.replace(/\D/g, '');
    if (digits.length < 6) { toast.error('Inserisci un numero valido (es. +39 335 1234567)'); return; }
    if (testNumbers.some(n => n.replace(/\D/g, '').slice(-9) === digits.slice(-9))) { toast.info('Numero già presente'); setNewTestNumber(''); return; }
    handleChange('whatsapp_test_numbers', [...testNumbers, raw]);
    setNewTestNumber('');
  };
  const removeTestNumber = (idx) => handleChange('whatsapp_test_numbers', testNumbers.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // Non sovrascrivere la chiave con stringa vuota se l'utente non l'ha toccata
      if (payload.waha_api_key === '' || payload.waha_api_key === undefined) delete payload.waha_api_key;
      payload.whatsapp_test_mode = !!payload.whatsapp_test_mode;
      payload.whatsapp_test_numbers = Array.isArray(payload.whatsapp_test_numbers) ? payload.whatsapp_test_numbers : [];
      if (payload.whatsapp_test_mode && payload.whatsapp_test_numbers.length === 0) {
        toast.error('Modalità test attiva ma nessun numero di test: aggiungi almeno un numero oppure disattiva la modalità test.');
        setSaving(false);
        return;
      }
      await axios.put(`${API}/settings`, payload);
      toast.success('Impostazioni WhatsApp salvate');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  const checkWahaStatus = async () => {
    setWahaBusy(true);
    try {
      const res = await axios.get(`${API}/whatsapp/status`);
      setWahaStatus(res.data.waha_session || (res.data.waha_configured ? 'NOT_CREATED' : 'NON CONFIGURATO'));
      if (!res.data.waha_configured) toast.error("Inserisci e salva prima l'URL del server WAHA");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella verifica');
    } finally { setWahaBusy(false); }
  };

  const startWahaSession = async () => {
    setWahaBusy(true);
    try {
      await axios.post(`${API}/whatsapp/waha/session/start`);
      toast.success('Sessione avviata: ora clicca "Mostra QR" e scansiona col telefono dello studio');
      setTimeout(checkWahaStatus, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Avvio sessione fallito');
    } finally { setWahaBusy(false); }
  };

  const showWahaQr = async () => {
    setWahaBusy(true);
    try {
      const res = await axios.get(`${API}/whatsapp/waha/qr`);
      setWahaQr(res.data.qr_base64);
      setQrVisible(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'QR non disponibile');
    } finally { setWahaBusy(false); }
  };

  const ToggleRow = ({ field, title, children, danger, testId }) => {
    const checked = form?.[field] !== false;
    return (
      <div className={`flex items-center justify-between py-3 border-t rounded-lg px-3 -mx-3 ${danger && !checked ? 'bg-red-50 border border-red-200' : ''}`} data-testid={`${testId}-row`}>
        <div className="pr-4">
          <p className="text-sm font-medium flex items-center gap-2">
            {title}
            {danger && !checked && <Badge className="bg-red-600 text-white hover:bg-red-600 text-[10px]">BLOCCATO</Badge>}
          </p>
          <p className="text-xs text-muted-foreground">{children}</p>
        </div>
        <Checkbox checked={checked} onCheckedChange={v => handleChange(field, v)} data-testid={`${testId}-checkbox`} />
      </div>
    );
  };

  return (
    <div data-testid="wa-config-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="outline" size="icon" onClick={() => navigate('/backoffice/whatsapp')} aria-label="Torna a WhatsApp" className="h-10 w-10 flex-shrink-0" data-testid="wa-config-back-button">
            <ArrowLeft size={18} />
          </Button>
          <PageTitle title="Configurazione WhatsApp" info="Motore di invio (Twilio o WAHA), collegamento del server WAHA con QR e interruttori generali dei messaggi automatici e manuali." />
        </div>
        <Button onClick={handleSave} disabled={saving || loading || !form} className="bg-[#238A92] hover:bg-[#0F5E66] text-white px-6" data-testid="wa-config-save-top">
          <Save size={14} className="mr-1" /> {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
      </div>

      {loading && (
        <div className="space-y-4">{[1, 2].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
      )}

      {!loading && loadError && (
        <Card data-testid="wa-config-error">
          <CardContent className="py-8 text-center space-y-3">
            <p className="text-sm text-muted-foreground">Impossibile caricare le impostazioni WhatsApp.</p>
            <Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-1" /> Riprova</Button>
          </CardContent>
        </Card>
      )}

      {!loading && form && (
        <div className="space-y-6 max-w-4xl">
          {/* Motore + WAHA */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><Server size={20} /> Motore di invio</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Motore attivo</Label>
                <Select value={form.whatsapp_engine || 'twilio'} onValueChange={v => handleChange('whatsapp_engine', v)}>
                  <SelectTrigger className="w-64" data-testid="wa-engine-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="twilio">Twilio (API ufficiale)</SelectItem>
                    <SelectItem value="waha">WAHA (server self-hosted)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Twilio usa l'API ufficiale; WAHA usa un tuo server esterno (Docker) col numero WhatsApp dello studio.</p>
              </div>

              {form.whatsapp_engine === 'waha' && (
                <div className="space-y-4 rounded-lg border border-[hsl(var(--border))] p-4 bg-[hsl(var(--secondary))]/30">
                  <div className="space-y-2">
                    <Label>URL server WAHA</Label>
                    <Input value={form.waha_base_url || ''} onChange={e => handleChange('waha_base_url', e.target.value)} placeholder="es. https://mio-tunnel.ngrok-free.dev" data-testid="waha-url-input" />
                    <p className="text-xs text-muted-foreground">URL pubblico del tuo server WAHA (VPS o Docker locale esposto con un tunnel).</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>API Key (X-Api-Key)</Label>
                      <div className="relative">
                        <Input
                          type={showKey ? 'text' : 'password'}
                          value={form.waha_api_key || ''}
                          onChange={e => handleChange('waha_api_key', e.target.value)}
                          placeholder="la chiave WAHA_API_KEY del server"
                          className="pr-10"
                          data-testid="waha-key-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowKey(s => !s)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#1F2A37]"
                          aria-label={showKey ? 'Nascondi chiave' : 'Mostra chiave'}
                          data-testid="waha-key-toggle"
                        >
                          {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">È la variabile <span className="font-mono">WAHA_API_KEY</span> impostata sul container Docker di WAHA.</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Nome sessione</Label>
                      <Input value={form.waha_session || 'default'} onChange={e => handleChange('waha_session', e.target.value)} data-testid="waha-session-input" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={checkWahaStatus} disabled={wahaBusy} data-testid="waha-status-button">
                      <RefreshCw size={14} className={`mr-1 ${wahaBusy ? 'animate-spin' : ''}`} /> Verifica stato
                    </Button>
                    <Button size="sm" className="bg-[#238A92] hover:bg-[#0F5E66] text-white" onClick={startWahaSession} disabled={wahaBusy} data-testid="waha-start-button">
                      <Play size={14} className="mr-1" /> {wahaBusy ? 'Attendere...' : 'Avvia sessione'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={showWahaQr} disabled={wahaBusy} data-testid="waha-qr-button">
                      <QrCode size={14} className="mr-1" /> Mostra QR
                    </Button>
                    {wahaStatus && (
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        wahaStatus === 'WORKING' ? 'bg-emerald-100 text-emerald-800' :
                        wahaStatus === 'SCAN_QR_CODE' ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-700'
                      }`} data-testid="waha-session-status">
                        Sessione: {sessionLabel(wahaStatus)}
                      </span>
                    )}
                  </div>

                  {/* QR inline (nessuna modale) */}
                  {qrVisible && (
                    <div className="rounded-lg border border-[hsl(var(--border))] bg-white p-4 flex flex-col sm:flex-row items-center gap-4" data-testid="waha-qr-panel">
                      {wahaQr ? (
                        <img src={`data:image/png;base64,${wahaQr}`} alt="QR WhatsApp" className="w-56 h-56 flex-shrink-0" data-testid="waha-qr-image" />
                      ) : (
                        <div className="w-56 h-56 flex items-center justify-center text-sm text-muted-foreground bg-[hsl(var(--secondary))]/40 rounded-lg flex-shrink-0">QR non disponibile</div>
                      )}
                      <div className="flex-1 space-y-3 text-sm">
                        <p className="font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Collega il telefono dello studio</p>
                        <p className="text-xs text-muted-foreground">WhatsApp → Impostazioni → Dispositivi collegati → Collega un dispositivo → scansiona questo QR. Il codice scade dopo pochi secondi: se non funziona, aggiornalo.</p>
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={showWahaQr} disabled={wahaBusy} data-testid="waha-qr-refresh"><RefreshCw size={14} className="mr-1" /> Aggiorna QR</Button>
                          <Button size="sm" variant="ghost" onClick={() => setQrVisible(false)} data-testid="waha-qr-close"><X size={14} className="mr-1" /> Nascondi</Button>
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">Flusso: salva → "Avvia sessione" → "Mostra QR" → scansiona col WhatsApp dello studio. Il webhook viene configurato automaticamente.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Interruttori */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}><ToggleLeft size={20} /> Interruttori generali</CardTitle>
            </CardHeader>
            <CardContent>
              <ToggleRow field="whatsapp_invio_attivo" title="Invio messaggi WhatsApp" danger testId="wa-sending">
                Interruttore generale. Se disattivato <strong>nessun messaggio WhatsApp</strong> viene inviato: né automatici (conferme, promemoria, risposte AI) né manuali dall'inbox. I messaggi in arrivo continuano a essere ricevuti.
              </ToggleRow>
              <ToggleRow field="whatsapp_solo_pazienti_registrati" title="Invia solo a pazienti registrati" testId="wa-only-registered">
                I messaggi <strong>automatici</strong> (risposte AI, conferme, promemoria) partono solo verso numeri presenti in anagrafica (pazienti, staff o medici). I numeri sconosciuti vengono segnalati nell'inbox come "richiede operatore" senza risposta automatica. L'invio manuale resta possibile.
              </ToggleRow>
              <ToggleRow field="whatsapp_ricezione_attiva" title="Ricezione messaggi in arrivo" testId="wa-ricezione">
                Se disattivata, i messaggi in arrivo dai pazienti vengono ignorati: non compaiono nell'inbox e l'assistente AI non risponde. L'invio dallo studio resta attivo.
              </ToggleRow>
              <ToggleRow field="whatsapp_auto_reply_enabled" title="Risposta automatica AI" testId="wa-auto-reply">
                L'assistente AI risponde da solo ai messaggi dei pazienti (orari, servizi, medici, prenotazioni). Se non sa rispondere, avvisa che un operatore ricontatterà e segnala la chat nell'inbox. Disattivabile anche per singola chat.
              </ToggleRow>
            </CardContent>
          </Card>

          {/* Modalità test */}
          <Card className={form.whatsapp_test_mode ? 'border-amber-300 bg-amber-50/40' : ''} data-testid="wa-test-mode-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                <FlaskConical size={20} /> Modalità test
                {form.whatsapp_test_mode && <Badge className="bg-amber-500 text-white hover:bg-amber-500 text-[10px]">ATTIVA</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-4" data-testid="wa-test-mode-row">
                <div className="pr-4">
                  <p className="text-sm font-medium">Elabora e invia solo da/verso i numeri di test</p>
                  <p className="text-xs text-muted-foreground">Da usare nell'ambiente di <strong>preview/test</strong>. Quando attiva, l'app ignora i messaggi in arrivo da numeri non in lista e blocca ogni invio (automatico o manuale) verso numeri non in lista. Anche se questo ambiente fosse collegato per errore al numero reale dello studio, non potrà mai rispondere a un paziente vero. In produzione lasciala disattivata.</p>
                </div>
                <Checkbox checked={!!form.whatsapp_test_mode} onCheckedChange={v => handleChange('whatsapp_test_mode', !!v)} data-testid="wa-test-mode-checkbox" />
              </div>

              <div className="space-y-2">
                <Label>Numeri di test consentiti</Label>
                <div className="flex gap-2">
                  <Input
                    value={newTestNumber}
                    onChange={e => setNewTestNumber(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTestNumber(); } }}
                    placeholder="es. +39 335 1234567"
                    className="max-w-xs"
                    data-testid="wa-test-number-input"
                  />
                  <Button type="button" variant="outline" onClick={addTestNumber} data-testid="wa-test-number-add"><Plus size={14} className="mr-1" /> Aggiungi</Button>
                </div>
                {testNumbers.length === 0 ? (
                  <p className="text-xs text-muted-foreground" data-testid="wa-test-numbers-empty">Nessun numero in lista. {form.whatsapp_test_mode && <span className="text-amber-700 font-medium">Con la modalità test attiva e la lista vuota nessun messaggio verrà elaborato né inviato.</span>}</p>
                ) : (
                  <ul className="flex flex-wrap gap-2" data-testid="wa-test-numbers-list">
                    {testNumbers.map((n, idx) => (
                      <li key={`${n}-${idx}`} className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--border))] bg-white pl-3 pr-1 py-1 text-sm font-mono" data-testid={`wa-test-number-item-${idx}`}>
                        {n}
                        <button type="button" onClick={() => removeTestNumber(idx)} className="h-6 w-6 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-red-700 hover:bg-red-50" aria-label={`Rimuovi ${n}`} data-testid={`wa-test-number-remove-${idx}`}>
                          <Trash2 size={13} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-muted-foreground">Il confronto avviene sulle ultime 9 cifre, quindi +39, 0039 o spazi non fanno differenza. Ricorda di salvare.</p>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between pb-12 flex-wrap gap-3">
            <Link to="/backoffice/whatsapp" className="text-sm text-[#238A92] hover:underline inline-flex items-center gap-1" data-testid="wa-config-back-link">
              <MessageCircle size={14} /> Torna all'inbox WhatsApp
            </Link>
            <Button onClick={handleSave} disabled={saving} className="bg-[#238A92] hover:bg-[#0F5E66] text-white px-8" data-testid="wa-config-save-bottom">
              <Save size={14} className="mr-1" /> {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
